import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RootState } from "configureStore";
import Currency from "components/format/currency/currency";
import Notice from "components/tw/notice";
import { btnLinkCls, btnPrimaryCls, btnSecondaryCls } from "components/tw/classes";
import {
  createInvoicesBulk,
  fetchInvoiceableOrders,
  type BulkInvoiceResult,
  type InvoiceableOrder,
} from "services/invoices.service";
import { invoiceErrorMessage } from "utils/invoice-errors";
import { canConfirmSalesInvoices } from "utils/salesInvoicePermissions";
import { setDestroy } from "components/modalx/modalx.slice";
import { formatOrderReferenceLabel } from "utils/invoice-number";
import SendByEmailCheckbox from "features/invoice-send/send-by-email-checkbox";
import useRememberedSendChoice from "features/invoice-send/use-remembered-send-choice";
import InvoiceableOrderRow from "./invoiceable-order-row";
import { lineProblem, type EditedLine } from "./invoiceable-order-lines";
import CreateInvoiceModalSkeleton from "./create-invoice-modal-skeleton";

type Props = {
  customerId: string;
  /** Called after anything was created, so the caller can refresh its list. */
  onDone?: () => void;
};

/** A failed row, with the order named the way the user knows it. */
type Failure = BulkInvoiceResult & { label: string };

const orderLabel = (order: InvoiceableOrder | undefined) =>
  order
    ? formatOrderReferenceLabel({ orderNumber: order.number, orderCode: order._number })
    : "";

/**
 * "Create invoice" on the customer's Invoices tab.
 *
 * Lists the orders that can still be invoiced — signed, one-off, not already
 * attached to an invoice — and turns the selection into invoices. Selecting N
 * orders produces N SEPARATE invoices, one per order: an invoice always covers
 * exactly one order.
 *
 * Two ways to finish:
 *   Draft           — create them as editable drafts with no numbers.
 *   Build invoices  — create and confirm immediately: each gets its number
 *                     and a PDF, and the customer is emailed when the
 *                     "email the customer" box is ticked.
 *
 * Long-term orders are absent by design; they are invoiced by their own
 * monthly schedule.
 */
export default function CreateInvoiceModal({ customerId, onDone }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const { role } = useSelector((state: RootState) => state.authReducer);
  // Same tier the backend requires for invoices/confirm and
  // create-invoices-bulk's "build" mode (SALES_INVOICE_CONFIRM_PERMISSIONS) —
  // a SalesMan can still Draft, matching orders/create-invoice's edit tier.
  const canBuild = canConfirmSalesInvoices(role);
  const [sendByEmail, setSendByEmail] = useRememberedSendChoice();

  const [orders, setOrders] = useState<InvoiceableOrder[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  /**
   * The lines as the user currently has them, per order — seeded from the
   * order and then edited in place. Held here rather than in each row so an
   * edit survives collapsing the row, and so submit can read every order's
   * lines without asking the rows for them.
   */
  const [linesByOrder, setLinesByOrder] = useState<Record<string, EditedLine[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<null | "draft" | "build">(null);
  const [failures, setFailures] = useState<Failure[]>([]);

  const load = useCallback(async () => {
    if (!companyId || !customerId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchInvoiceableOrders({ companyId, customerId });
      const list = data.orders || [];
      setOrders(list);
      // Reset edits to what the orders actually say — a reload happens after a
      // partial failure, and carrying stale edits across it would show numbers
      // that no longer match the order behind them.
      setLinesByOrder(
        Object.fromEntries(
          list.map((order) => [order.id, (order.lines || []).map((l) => ({ ...l }))])
        )
      );
      // A selected order that dropped out of the list cannot be submitted.
      setSelected((current) => current.filter((id) => list.some((o) => o.id === id)));
    } catch (e: any) {
      setOrders([]);
      setLinesByOrder({});
      setLoadError(
        e?.response?.data?.message || e?.message || t("error", { defaultValue: "Error" })
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, customerId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const changeLine = (orderId: string, externalCode: string, patch: Partial<EditedLine>) =>
    setLinesByOrder((current) => ({
      ...current,
      [orderId]: (current[orderId] || []).map((line) =>
        line.externalCode === externalCode ? { ...line, ...patch } : line
      ),
    }));

  /** Whether an order's lines differ from what the order itself says. */
  const isEdited = (order: InvoiceableOrder) => {
    const current = linesByOrder[order.id] || [];
    const original = order.lines || [];
    if (current.filter((l) => !l.removed).length !== original.length) return true;
    return current.some((line) => {
      const source = original.find((l) => l.externalCode === line.externalCode);
      if (!source) return true;
      return (
        String(line.quantity) !== String(source.quantity) ||
        String(line.price) !== String(source.price)
      );
    });
  };

  const toggle = (orderId: string) =>
    setSelected((current) =>
      current.includes(orderId)
        ? current.filter((id) => id !== orderId)
        : [...current, orderId]
    );

  const allSelected = orders.length > 0 && selected.length === orders.length;
  const toggleAll = () => setSelected(allSelected ? [] : orders.map((order) => order.id));

  /**
   * Selected orders with a line that cannot be invoiced as it stands — an empty
   * or zero quantity, say. Submitting them would issue a legal invoice billing
   * nothing, so the buttons stay off until they are fixed or deselected.
   */
  const invalidSelected = useMemo(
    () =>
      selected.filter((orderId) =>
        (linesByOrder[orderId] || []).some((line) => lineProblem(line))
      ),
    [selected, linesByOrder]
  );

  /** Total of what will actually be invoiced, edits included. */
  const selectedTotal = useMemo(
    () =>
      selected.reduce((sum, orderId) => {
        const lines = linesByOrder[orderId] || [];
        return (
          sum +
          lines
            .filter((line) => !line.removed)
            .reduce(
              (lineSum, line) =>
                lineSum + (Number(line.quantity) || 0) * (Number(line.price) || 0),
              0
            )
        );
      }, 0),
    [selected, linesByOrder]
  );

  const canSubmit = selected.length > 0 && invalidSelected.length === 0 && !submitting;

  const submit = async (mode: "draft" | "build") => {
    if (!companyId || !customerId || !canSubmit) return;
    setSubmitting(mode);
    setFailures([]);
    // Names for the report, taken now: a failed order may leave the list on
    // the reload below (an invoice already exists for it), and a UUID is not
    // something the user can recognise.
    const labels = new Map(orders.map((order) => [order.id, orderLabel(order)]));
    try {
      const result = await createInvoicesBulk({
        companyId,
        customerId,
        mode,
        // Drafts are never emailed; only the invoices built right now are.
        send: mode === "build" && sendByEmail,
        orders: selected.map((orderId) => ({
          id: orderId,
          // The lines to keep, with the user's quantities and prices.
          lines: (linesByOrder[orderId] || [])
            .filter((line) => !line.removed)
            .map((line) => ({
              externalCode: line.externalCode,
              quantity: Number(line.quantity),
              price: Number(line.price),
            })),
        })),
      });

      const failed = (result.results || []).filter((row) => !row.ok);
      if (result.createdCount > 0) {
        toast.success(
          mode === "build"
            ? t("invoices_built", {
                defaultValue: "{{count}} invoice(s) created and confirmed",
                count: result.createdCount,
              })
            : t("invoices_drafted", {
                defaultValue: "{{count}} draft invoice(s) created",
                count: result.createdCount,
              })
        );
        onDone?.();
      }

      if (failed.length) {
        // Keep the modal open listing what did not work: the rest already
        // succeeded, and closing would hide which orders still need attention.
        setFailures(
          failed.map((row) => ({
            ...row,
            label: labels.get(row.orderId) || t("order", { defaultValue: "Order" }),
          }))
        );
        setSelected(failed.map((row) => row.orderId));
        await load();
        return;
      }

      dispatch(setDestroy());
    } catch (e: any) {
      toast.error(invoiceErrorMessage(e, t));
    } finally {
      setSubmitting(null);
    }
  };

  /** One failure, in words: "already invoiced" reads as such, not as an error code. */
  const failureText = (row: Failure) =>
    row.alreadyExists
      ? t("create_invoice_already_invoiced", {
          defaultValue: "an invoice already exists for this order",
        })
      : invoiceErrorMessage(row, t);

  if (loading) return <CreateInvoiceModalSkeleton />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-500">
        {t("create_invoice_modal_hint", {
          defaultValue:
            "Fully signed orders of this customer without an invoice yet. Long-term orders are invoiced by their monthly schedule and are not listed here. One invoice is created per selected order.",
        })}
      </p>

      {loadError && (
        <Notice tone="critical" title={t("error", { defaultValue: "Error" })}>
          <p>{loadError}</p>
        </Notice>
      )}

      {failures.length > 0 && (
        <Notice
          tone="warning"
          title={t("create_invoice_partial_failure", {
            defaultValue: "Some orders could not be invoiced",
          })}
        >
          <ul className="space-y-0.5">
            {failures.map((row) => (
              <li key={row.orderId} className="text-xs">
                <span className="font-medium">{row.label}</span>: {failureText(row)}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {!loadError && orders.length === 0 && (
        <Notice tone="info">
          <p>
            {t("create_invoice_no_orders", {
              defaultValue:
                "No orders are ready to invoice. An order must be fully signed and not already invoiced.",
            })}
          </p>
        </Notice>
      )}

      {orders.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <button type="button" className={btnLinkCls} onClick={toggleAll}>
              {allSelected
                ? t("deselect_all", { defaultValue: "Deselect all" })
                : t("select_all", { defaultValue: "Select all" })}
            </button>
            <span className="text-xs text-zinc-500">
              {t("selected_of_orders", {
                defaultValue: "{{selected}} of {{total}} orders selected",
                selected: selected.length,
                total: orders.length,
              })}
            </span>
          </div>

          <div className="space-y-2">
            {orders.map((order) => (
              <InvoiceableOrderRow
                key={order.id}
                order={order}
                lines={linesByOrder[order.id] || []}
                selected={selected.includes(order.id)}
                disabled={!!submitting}
                edited={isEdited(order)}
                onToggle={toggle}
                onLineChange={changeLine}
              />
            ))}
          </div>

          {invalidSelected.length > 0 && (
            <Notice tone="critical">
              <p>
                {t("create_invoice_lines_invalid", {
                  defaultValue:
                    "{{count}} selected order(s) have a product with no quantity. Enter a quantity above 0, remove the line, or deselect the order.",
                  count: invalidSelected.length,
                })}
              </p>
            </Notice>
          )}

          <p className="text-xs text-zinc-500">
            {canBuild
              ? t("create_invoice_actions_hint", {
                  defaultValue:
                    "Open an order to edit its products, quantities and prices before invoicing. Draft saves editable drafts without numbers. Build invoices confirms immediately: assigns the invoice number and generates the PDF; tick the box to email the customer as well.",
                })
              : t("create_invoice_actions_hint_no_build", {
                  defaultValue:
                    "Open an order to edit its products, quantities and prices before invoicing. Draft saves editable drafts without numbers — assigning a number, PDF and email needs the Master, Admin, Signature or “Access all customers” permission.",
                })}
          </p>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
              {selectedTotal > 0 ? <Currency value={selectedTotal} isSymbol /> : ""}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={btnSecondaryCls}
                onClick={() => submit("draft")}
                disabled={!canSubmit}
              >
                {submitting === "draft" ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                ) : null}
                {t("save_as_draft_count", {
                  defaultValue: "Draft ({{count}})",
                  count: selected.length,
                })}
              </button>
              {/* The backend refuses the WHOLE bulk request for a role
                  outside the confirm tier (customers/create-invoices-bulk,
                  mode "build") — hidden rather than shown-then-403'd. */}
              {canBuild && (
                <>
                  <SendByEmailCheckbox
                    checked={sendByEmail}
                    onChange={setSendByEmail}
                    disabled={!!submitting}
                  />
                  <button
                    type="button"
                    className={btnPrimaryCls}
                    onClick={() => submit("build")}
                    disabled={!canSubmit}
                  >
                    {submitting === "build" ? (
                      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    ) : null}
                    {t("build_invoices_count", {
                      defaultValue: "Build invoices ({{count}})",
                      count: selected.length,
                    })}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
